import { AxiosResponse } from 'axios';
import { FormikHelpers } from 'formik';
import { updateActiveProfileForm } from 'pages/profile/profile.actions';
import { otpCodeFieldName } from 'pages/profile/profile.config';
import { selectVerifiedContactsApiConfig } from 'pages/profile/profile.selectors';
import React, { useEffect, useState } from 'react';
import { useDispatch, useStore } from 'react-redux';
import {
  invalidateLoadedResourceIfPresent,
  requestDataIfNecessary,
} from 'services/api/api.actions';
import apiRequest from 'services/api/api.config';
import { unprocessableEntity_422 } from 'services/api/api.statusCodes';
import {
  resetOtpStateAction,
  setOtpStateAction,
} from 'services/otp/otp.actions';
import {
  makeSelectPostOtpCheckApiConfig,
  selectOtpJwt,
} from 'services/otp/otp.selectors';
import { OtpSetStatePayload } from 'services/otp/otp.types';
import { getUserProfileEndpoint } from 'services/user/user.config';
import { selectGetUserProfileApiConfig } from 'services/user/user.selectors';
import required from 'utils/validation/required';
import {
  validateFieldSync,
  validateFormSync,
} from 'utils/validation/validate-generators';
import { OtpFormValue } from './OtpModal.config';
import { ValuesContactType } from './partials/OtpModalBody.types';

export function useGetOtpData() {
  return React.useMemo(
    () => ({
      initialValues: {
        [otpCodeFieldName]: '',
      },
      validate: validateFormSync({
        [otpCodeFieldName]: validateFieldSync(required),
      }),
    }),
    [],
  );
}

export function useCreateHandleOtpCheckSubmit(
  valuesContact: ValuesContactType,
  isVerify: boolean,
  setShowSuccessMessage: React.Dispatch<React.SetStateAction<boolean>>,
  closeModalCallback: () => void,
  onIncorrectOtpSubmitError?: () => void,
) {
  const reduxStore = useStore();
  const reduxDispatch = useDispatch();

  return React.useCallback(
    async (
      { [otpCodeFieldName]: otp }: OtpFormValue,
      formikHelpers: FormikHelpers<OtpFormValue>,
    ) => {
      const otpValue = otp;

      const state = reduxStore.getState();

      // Generated as a response from /otp-security-ms/ms/contact/generate
      const jwt = selectOtpJwt(state);

      if (jwt == null) {
        throw new Error(
          'Cannot invoke otp check, jwt from generate step is missing',
        );
      }

      try {
        const apiConfig = makeSelectPostOtpCheckApiConfig({
          otp: otpValue,
          isVerify: isVerify,
          jwt,
          ...valuesContact,
        })(state);
        const response = await apiRequest.request(apiConfig.requestConfig);
        formikHelpers.resetForm();

        reduxDispatch(resetOtpStateAction());

        if (valuesContact) {
          reduxDispatch(
            invalidateLoadedResourceIfPresent({
              resourceId: getUserProfileEndpoint,
            }),
          );
          const getUserProfileApiConfig = selectGetUserProfileApiConfig(state);

          reduxStore.dispatch(requestDataIfNecessary(getUserProfileApiConfig));

          //Hides form
          reduxDispatch(updateActiveProfileForm(null));
        }

        const verifiedContactsApiConfig =
          selectVerifiedContactsApiConfig(state);

        //Calls getContactCertification
        reduxDispatch(requestDataIfNecessary(verifiedContactsApiConfig));
        setShowSuccessMessage(true);
        setTimeout(() => {
          closeModalCallback();
        }, 1500);
        return response;
      } catch (fetchError) {
        if (fetchError.response?.data) {
          const errorResponse = fetchError.response as AxiosResponse;

          if (errorResponse?.status === unprocessableEntity_422) {
            formikHelpers.resetForm();
            const tempStateObj: OtpSetStatePayload = {
              hasError: true,
              hintText: '',
              errorInfos: errorResponse.data.error.errorInfos,
              counterCheck: errorResponse.data.counterCheck ?? 4,
            };
            reduxDispatch(setOtpStateAction(tempStateObj));
          }

          onIncorrectOtpSubmitError?.();
        }
        return fetchError;
      }
    },
    [
      closeModalCallback,
      isVerify,
      onIncorrectOtpSubmitError,
      reduxDispatch,
      reduxStore,
      setShowSuccessMessage,
      valuesContact,
    ],
  );
}

export const useIsNoteVisible: (isGenerateLimitReached: boolean) => boolean = (
  isGenerateLimitReached,
) => {
  const [showNote, setShowNote] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    const isVisible = isGenerateLimitReached;
    let timer: ReturnType<typeof setTimeout>;

    if (isVisible) {
      timer = setTimeout(() => {
        dispatch(resetOtpStateAction());
        setShowNote(false);
      }, 1_800_000);
    }
    setShowNote(isVisible);

    return () => clearTimeout(timer);
  }, [isGenerateLimitReached, dispatch]);

  return showNote;
};

export function useResetOtpStateOnUnmount() {
  const dispatch = useDispatch();

  useEffect(() => {
    return () => {
      dispatch(resetOtpStateAction());
    };
  }, [dispatch]);
}
